import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Divider,
  ClickAwayListener,
} from '@material-ui/core';
import { AppContext } from '../../../modules/AppContext';
import {
  getPendingFundReconciliation,
  searchMasterFunds,
  confirmFundReconciliationMatch,
  createMasterFundAndResolve,
} from '../../../modules/admin';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    padding: '20px',
    margin: '0',
  },
  content: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  rawField: {
    marginRight: theme.spacing(4),
  },
  searchWrap: {
    position: 'relative',
    maxWidth: 480,
  },
  searchResults: {
    position: 'absolute',
    top: '100%',
    width: '100%',
    zIndex: 3,
  },
  searchResultRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  section: {
    marginTop: theme.spacing(3),
  },
  createFields: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

const emptyCreateForm = {
  fundName: '',
  ticker: '',
  cusip: '',
  isin: '',
};

export const FundReconciliation = () => {
  const classes = useStyles();
  const { showToast } = useContext(AppContext);

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchVisible, setSearchVisible] = useState(false);
  const [selectedMasterFund, setSelectedMasterFund] = useState(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);

  const currentItem = items[currentIndex];

  const loadPending = async () => {
    setLoading(true);
    const pending = await getPendingFundReconciliation();
    setItems(pending);
    setCurrentIndex(0);
    setLoading(false);
  };

  useEffect(() => {
    loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetSelection = () => {
    setSearchTerm('');
    setSearchResults([]);
    setSearchVisible(false);
    setSelectedMasterFund(null);
    setShowCreateForm(false);
    setCreateForm(emptyCreateForm);
  };

  useEffect(() => {
    if (searchTerm.length > 1) {
      const timeout = setTimeout(async () => {
        setSearchResults(await searchMasterFunds(searchTerm));
        setSearchVisible(true);
      }, 250);

      return () => clearTimeout(timeout);
    }

    setSearchVisible(false);
    return () => {};
  }, [searchTerm]);

  const advanceToNext = () => {
    resetSelection();
    setItems((prev) => prev.filter((x) => x.id !== currentItem.id));
    setCurrentIndex((prev) => (prev >= items.length - 1 ? 0 : prev));
  };

  const handleConfirmMatch = async (masterFundId) => {
    if (!masterFundId || !currentItem) return;

    setSubmitting(true);
    const ok = await confirmFundReconciliationMatch(
      currentItem.id,
      masterFundId
    );
    setSubmitting(false);

    if (ok) {
      showToast({ message: 'Match confirmed.', type: 'success' });
      advanceToNext();
    } else {
      showToast({ message: 'Failed to confirm match.', type: 'error' });
    }
  };

  const handleCreateMasterFund = async () => {
    if (!currentItem) return;

    if (!createForm.fundName) {
      showToast({ message: 'Fund name is required.', type: 'error' });
      return;
    }

    setSubmitting(true);
    const newMasterFundId = await createMasterFundAndResolve({
      fundReconciliationId: currentItem.id,
      ...createForm,
    });
    setSubmitting(false);

    if (newMasterFundId) {
      showToast({ message: 'New master fund created and resolved.', type: 'success' });
      advanceToNext();
    } else {
      showToast({ message: 'Failed to create master fund.', type: 'error' });
    }
  };

  const openCreateForm = () => {
    setSearchVisible(false);
    setShowCreateForm(true);
    setCreateForm({
      fundName: currentItem?.rawDescription || '',
      ticker: currentItem?.rawTicker || '',
      cusip: currentItem?.rawCusip || '',
      isin: currentItem?.rawIsin || '',
    });
  };

  return (
    <div className={classes.wrapper}>
      <Paper elevation={1} className={classes.content}>
        <Typography variant="h6" gutterBottom>
          Fund Reconciliation
        </Typography>

        {loading && <CircularProgress size={24} />}

        {!loading && items.length === 0 && (
          <Typography variant="body1">
            All caught up! No items are pending reconciliation.
          </Typography>
        )}

        {!loading && currentItem && (
          <Box>
            <Typography variant="caption" color="textSecondary">
              Item {currentIndex + 1} of {items.length}
            </Typography>

            <Box display="flex" alignItems="center" mt={1} mb={2}>
              <Chip
                label={currentItem.sourceType}
                color="primary"
                size="small"
                style={{ marginRight: 12 }}
              />
              <Typography variant="caption" color="textSecondary">
                Queued {new Date(currentItem.queuedAtUtc).toLocaleString()}
              </Typography>
            </Box>

            <Box display="flex" flexWrap="wrap" mb={2}>
              <Typography className={classes.rawField} variant="body2">
                <strong>Ticker:</strong> {currentItem.rawTicker || '—'}
              </Typography>
              <Typography className={classes.rawField} variant="body2">
                <strong>CUSIP:</strong> {currentItem.rawCusip || '—'}
              </Typography>
              <Typography className={classes.rawField} variant="body2">
                <strong>ISIN:</strong> {currentItem.rawIsin || '—'}
              </Typography>
              <Typography className={classes.rawField} variant="body2">
                <strong>Description:</strong> {currentItem.rawDescription || '—'}
              </Typography>
              {currentItem.sourceType !== 'Fund' && (
                <>
                  <Typography className={classes.rawField} variant="body2">
                    <strong>Account ID:</strong> {currentItem.accountId || '—'}
                  </Typography>
                  <Typography className={classes.rawField} variant="body2">
                    <strong>Participant ID:</strong>{' '}
                    {currentItem.participantId || '—'}
                  </Typography>
                  <Typography className={classes.rawField} variant="body2">
                    <strong>Distributor:</strong>{' '}
                    {currentItem.distributorName || '—'}
                  </Typography>
                  <Typography className={classes.rawField} variant="body2">
                    <strong>Fund Lineup ID:</strong>{' '}
                    {currentItem.fundLineUpId || '—'}
                  </Typography>
                </>
              )}
            </Box>

            <Divider />

            {currentItem.candidateMasterFundId && (
              <Box className={classes.section}>
                <Typography variant="subtitle2" gutterBottom>
                  Suggested match
                </Typography>
                <Box display="flex" alignItems="center">
                  <Typography variant="body1" style={{ marginRight: 12 }}>
                    {currentItem.candidateMasterFundName} (
                    {currentItem.candidateMasterFundTicker || 'no ticker'})
                  </Typography>
                  {currentItem.candidateFundConfidenceScore != null && (
                    <Chip
                      size="small"
                      label={`${Math.round(
                        currentItem.candidateFundConfidenceScore * 100
                      )}% · ${currentItem.candidateMatchStrategy || ''}`}
                      style={{ marginRight: 12 }}
                    />
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={submitting}
                    onClick={() =>
                      handleConfirmMatch(currentItem.candidateMasterFundId)
                    }
                  >
                    Confirm this match
                  </Button>
                </Box>
              </Box>
            )}

            <Box className={classes.section}>
              <Typography variant="subtitle2" gutterBottom>
                Search for a different match
              </Typography>
              <ClickAwayListener onClickAway={() => setSearchVisible(false)}>
                <Box className={classes.searchWrap}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Search by ticker or fund name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => searchTerm.length > 1 && setSearchVisible(true)}
                  />
                  {searchVisible && (
                    <Paper elevation={3} className={classes.searchResults}>
                      {searchResults.length === 0 && (
                        <Box p={2}>
                          <Typography variant="body2">No matches found.</Typography>
                        </Box>
                      )}
                      {searchResults.map((mf) => (
                        <Box
                          key={mf.id}
                          className={classes.searchResultRow}
                          p={1}
                          display="flex"
                          justifyContent="space-between"
                          onClick={() => {
                            setSelectedMasterFund(mf);
                            setSearchVisible(false);
                            setSearchTerm('');
                          }}
                        >
                          <Typography variant="body2">{mf.ticker || '—'}</Typography>
                          <Typography variant="body2">{mf.fundName}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Box>
              </ClickAwayListener>

              {selectedMasterFund && (
                <Box display="flex" alignItems="center" mt={2}>
                  <Typography variant="body1" style={{ marginRight: 12 }}>
                    Selected: {selectedMasterFund.fundName} (
                    {selectedMasterFund.ticker || 'no ticker'})
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={submitting}
                    onClick={() => handleConfirmMatch(selectedMasterFund.id)}
                  >
                    Confirm this match
                  </Button>
                </Box>
              )}
            </Box>

            <Box className={classes.section}>
              {!showCreateForm && (
                <Button variant="outlined" onClick={openCreateForm}>
                  Create new master fund
                </Button>
              )}

              {showCreateForm && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Create new master fund
                  </Typography>
                  <Box className={classes.createFields}>
                    <TextField
                      label="Fund Name"
                      variant="outlined"
                      size="small"
                      value={createForm.fundName}
                      onChange={(e) =>
                        setCreateForm((prev) => ({ ...prev, fundName: e.target.value }))
                      }
                    />
                    <TextField
                      label="Ticker"
                      variant="outlined"
                      size="small"
                      value={createForm.ticker}
                      onChange={(e) =>
                        setCreateForm((prev) => ({ ...prev, ticker: e.target.value }))
                      }
                    />
                    <TextField
                      label="CUSIP"
                      variant="outlined"
                      size="small"
                      value={createForm.cusip}
                      onChange={(e) =>
                        setCreateForm((prev) => ({ ...prev, cusip: e.target.value }))
                      }
                    />
                    <TextField
                      label="ISIN"
                      variant="outlined"
                      size="small"
                      value={createForm.isin}
                      onChange={(e) =>
                        setCreateForm((prev) => ({ ...prev, isin: e.target.value }))
                      }
                    />
                  </Box>
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      onClick={handleCreateMasterFund}
                      style={{ marginRight: 12 }}
                    >
                      Create &amp; resolve
                    </Button>
                    <Button
                      variant="text"
                      disabled={submitting}
                      onClick={() => setShowCreateForm(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </div>
  );
};
